import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'

interface AppHeaderProps {
  /** Centre slot: the wizard's "Step x of n". */
  children?: ReactNode
  /** 0–100. Draws a thin bar along the header's bottom edge. */
  progress?: number
  progressLabel?: string
  progressNow?: number
  progressMax?: number
}

/**
 * The slim bar above the onboarding wizard and the resume gate: logo, an
 * optional centre slot, the theme toggle and the account. 48px on phones,
 * 56px from `sm`, with the step progress drawn across the full width.
 */
export function AppHeader({ children, progress, progressLabel, progressNow, progressMax }: AppHeaderProps) {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)

  return (
    <header
      className="sticky top-0 z-30 border-b border-line bg-surface/90 backdrop-blur supports-[backdrop-filter]:bg-surface/80"
      style={{ paddingTop: 'var(--safe-top)' }}
    >
      <div className="relative mx-auto flex h-12 w-full max-w-[1280px] items-center gap-2 px-3 sm:h-14 sm:gap-3 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex h-11 shrink-0 items-center gap-2.5 rounded-field pr-1.5 text-[17px] font-semibold tracking-tight text-ink"
        >
          <LogoMark />
          <span className="hidden xs:inline">JobSlide</span>
        </Link>

        <div className="min-w-0 flex-1 text-center sm:text-left">{children}</div>

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
          <ThemeToggle />
          {user && (
            <>
              <span className="hidden max-w-[220px] truncate text-[13px] text-ink-muted md:block" title={user.email}>
                {user.email}
              </span>
              <span className="hidden h-4 w-px bg-line md:block" aria-hidden="true" />
              <button
                type="button"
                onClick={() => void logout()}
                aria-label="Log out"
                className="inline-flex h-11 min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-field px-2 text-[13.5px] font-medium text-ink-soft transition-colors hover:bg-surface-muted hover:text-ink sm:px-3"
              >
                <LogoutIcon />
                <span className="hidden sm:inline">Log out</span>
              </button>
            </>
          )}
        </div>
      </div>

      {typeof progress === 'number' && (
        <div
          role="progressbar"
          aria-label={progressLabel}
          aria-valuenow={progressNow ?? Math.round(progress)}
          aria-valuemin={progressNow !== undefined ? 1 : 0}
          aria-valuemax={progressMax ?? 100}
          className="absolute inset-x-0 -bottom-px h-[3px] overflow-hidden bg-line/60"
        >
          <div
            className="h-full bg-brand transition-[width] duration-500 ease-out"
            style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
          />
        </div>
      )}
    </header>
  )
}

/** The JobSlide mark (a forward slide: two chevrons). Same as the dashboard shell. `text-oncolor` so it reads in both themes. */
export function LogoMark({ className }: { className?: string }) {
  return (
    <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-brand text-oncolor', className)}>
      <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3.25 3.5 6.75 7l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
        <path d="M7.25 3.5 10.75 7l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

/** One tap flips light and dark. The full choice (incl. "system") lives in the account sheet. */
export function ThemeToggle({ className }: { className?: string }) {
  const [, resolved, setTheme] = useTheme()
  const dark = resolved === 'dark'
  return (
    <button
      type="button"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Light theme' : 'Dark theme'}
      className={cn(
        'grid h-11 w-11 shrink-0 place-items-center rounded-field text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink',
        className,
      )}
    >
      {dark ? <SunIcon /> : <MoonIcon />}
    </button>
  )
}

function SunIcon() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M16.5 12.2A7 7 0 0 1 7.8 3.5a7 7 0 1 0 8.7 8.7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-5A1.5 1.5 0 0 0 3 3.5v11A1.5 1.5 0 0 0 4.5 16h5a1.5 1.5 0 0 0 1.5-1.5V13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M7.5 9h8m0 0-2.5-2.5M15.5 9 13 11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
